import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  Timeline,
  CreateTimelineInput,
  UpdateTimelineInput,
  CreateTimelineSectionInput,
  UpdateTimelineSectionInput,
  CreateTimelineEntryInput,
  UpdateTimelineEntryInput,
  Checklist,
  CreateChecklistInput,
  UpdateChecklistInput,
  CreateChecklistSectionInput,
  UpdateChecklistSectionInput,
  CreateChecklistItemInput,
  UpdateChecklistItemInput,
  Kibuddim,
  CreateKibuddimInput,
  UpdateKibuddimInput,
  CreateKibuddimSectionInput,
  UpdateKibuddimSectionInput,
  CreateKibuddimRoleInput,
  UpdateKibuddimRoleInput,
} from '@peles/shared';
import * as eventOpsApi from '../api/event-ops';

// ─── Timeline Hooks ─────────────────────────────────────────────────────────

export function useTimelines(eventId?: string) {
  return useQuery<Timeline[]>({
    queryKey: ['timelines', eventId ?? 'all'],
    queryFn: () => eventOpsApi.getTimelines(eventId),
  });
}

export function useTimeline(id: string) {
  return useQuery<Timeline>({
    queryKey: ['timelines', 'detail', id],
    queryFn: () => eventOpsApi.getTimeline(id),
    enabled: !!id,
  });
}

export function useCreateTimeline() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateTimelineInput) => eventOpsApi.createTimeline(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timelines'] });
    },
  });
}

export function useUpdateTimeline() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdateTimelineInput }) =>
      eventOpsApi.updateTimeline(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timelines'] });
    },
  });
}

export function useDeleteTimeline() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => eventOpsApi.deleteTimeline(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timelines'] });
    },
  });
}

export function useCloneTimeline() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; eventId?: string; title?: string }) =>
      eventOpsApi.cloneTimeline(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timelines'] });
    },
  });
}

export function useAddTimelineSection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ timelineId, ...input }: CreateTimelineSectionInput & { timelineId: string }) =>
      eventOpsApi.addTimelineSection(timelineId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timelines'] });
    },
  });
}

export function useUpdateTimelineSection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      sectionId,
      updates,
    }: {
      sectionId: string;
      updates: UpdateTimelineSectionInput;
    }) => eventOpsApi.updateTimelineSection(sectionId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timelines'] });
    },
  });
}

export function useDeleteTimelineSection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sectionId: string) => eventOpsApi.deleteTimelineSection(sectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timelines'] });
    },
  });
}

export function useAddTimelineEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sectionId, ...input }: CreateTimelineEntryInput & { sectionId: string }) =>
      eventOpsApi.addTimelineEntry(sectionId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timelines'] });
    },
  });
}

export function useUpdateTimelineEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ entryId, updates }: { entryId: string; updates: UpdateTimelineEntryInput }) =>
      eventOpsApi.updateTimelineEntry(entryId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timelines'] });
    },
  });
}

export function useDeleteTimelineEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (entryId: string) => eventOpsApi.deleteTimelineEntry(entryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timelines'] });
    },
  });
}

// ─── Checklist Hooks ────────────────────────────────────────────────────────

export function useChecklists(eventId?: string) {
  return useQuery<Checklist[]>({
    queryKey: ['checklists', eventId ?? 'all'],
    queryFn: () => eventOpsApi.getChecklists(eventId),
  });
}

export function useChecklist(id: string) {
  return useQuery<Checklist>({
    queryKey: ['checklists', 'detail', id],
    queryFn: () => eventOpsApi.getChecklist(id),
    enabled: !!id,
  });
}

export function useCreateChecklist() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateChecklistInput) => eventOpsApi.createChecklist(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checklists'] });
    },
  });
}

export function useUpdateChecklist() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdateChecklistInput }) =>
      eventOpsApi.updateChecklist(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checklists'] });
    },
  });
}

export function useDeleteChecklist() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => eventOpsApi.deleteChecklist(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checklists'] });
    },
  });
}

export function useCloneChecklist() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; eventId?: string; title?: string }) =>
      eventOpsApi.cloneChecklist(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checklists'] });
    },
  });
}

export function useAddChecklistSection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      checklistId,
      ...input
    }: CreateChecklistSectionInput & { checklistId: string }) =>
      eventOpsApi.addChecklistSection(checklistId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checklists'] });
    },
  });
}

export function useUpdateChecklistSection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      sectionId,
      updates,
    }: {
      sectionId: string;
      updates: UpdateChecklistSectionInput;
    }) => eventOpsApi.updateChecklistSection(sectionId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checklists'] });
    },
  });
}

export function useDeleteChecklistSection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sectionId: string) => eventOpsApi.deleteChecklistSection(sectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checklists'] });
    },
  });
}

export function useAddChecklistItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sectionId, ...input }: CreateChecklistItemInput & { sectionId: string }) =>
      eventOpsApi.addChecklistItem(sectionId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checklists'] });
    },
  });
}

export function useUpdateChecklistItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, updates }: { itemId: string; updates: UpdateChecklistItemInput }) =>
      eventOpsApi.updateChecklistItem(itemId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checklists'] });
    },
  });
}

export function useDeleteChecklistItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => eventOpsApi.deleteChecklistItem(itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checklists'] });
    },
  });
}

export function useToggleChecklistItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, isDone }: { itemId: string; isDone: boolean }) =>
      eventOpsApi.toggleChecklistItem(itemId, isDone),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checklists'] });
    },
  });
}

export function useChecklistItemsForCalendar(year: number, month: number) {
  const dateFrom = new Date(year, month, 1).toISOString().slice(0, 10);
  const dateTo = new Date(year, month + 1, 0).toISOString().slice(0, 10);
  return useQuery({
    queryKey: ['checklist-items-calendar', year, month],
    queryFn: () => eventOpsApi.getChecklistItemsForCalendar({ dateFrom, dateTo }),
  });
}

export function useChecklistItemsForDateRange(dateFrom: string, dateTo: string, enabled = true) {
  return useQuery({
    queryKey: ['checklist-items-calendar-range', dateFrom, dateTo],
    queryFn: () => eventOpsApi.getChecklistItemsForCalendar({ dateFrom, dateTo }),
    enabled,
  });
}

// ─── Kibuddim Hooks ────────────────────────────────────────────────────────

export function useKibuddimList(eventId?: string) {
  return useQuery<Kibuddim[]>({
    queryKey: ['kibuddim', eventId ?? 'all'],
    queryFn: () => eventOpsApi.getKibuddimList(eventId),
  });
}

export function useKibuddimSingle(id: string) {
  return useQuery<Kibuddim>({
    queryKey: ['kibuddim', 'detail', id],
    queryFn: () => eventOpsApi.getKibuddimSingle(id),
    enabled: !!id,
  });
}

export function useCreateKibuddim() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateKibuddimInput) => eventOpsApi.createKibuddim(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kibuddim'] });
    },
  });
}

export function useUpdateKibuddim() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdateKibuddimInput }) =>
      eventOpsApi.updateKibuddim(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kibuddim'] });
    },
  });
}

export function useDeleteKibuddim() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => eventOpsApi.deleteKibuddim(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kibuddim'] });
    },
  });
}

export function useCloneKibuddim() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; eventId?: string; title?: string }) =>
      eventOpsApi.cloneKibuddim(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kibuddim'] });
    },
  });
}

export function useAddKibuddimSection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ kibuddimId, ...input }: CreateKibuddimSectionInput & { kibuddimId: string }) =>
      eventOpsApi.addKibuddimSection(kibuddimId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kibuddim'] });
    },
  });
}

export function useUpdateKibuddimSection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      sectionId,
      updates,
    }: {
      sectionId: string;
      updates: UpdateKibuddimSectionInput;
    }) => eventOpsApi.updateKibuddimSection(sectionId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kibuddim'] });
    },
  });
}

export function useDeleteKibuddimSection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sectionId: string) => eventOpsApi.deleteKibuddimSection(sectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kibuddim'] });
    },
  });
}

export function useAddKibuddimRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sectionId, ...input }: CreateKibuddimRoleInput & { sectionId: string }) =>
      eventOpsApi.addKibuddimRole(sectionId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kibuddim'] });
    },
  });
}

export function useUpdateKibuddimRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ roleId, updates }: { roleId: string; updates: UpdateKibuddimRoleInput }) =>
      eventOpsApi.updateKibuddimRole(roleId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kibuddim'] });
    },
  });
}

export function useDeleteKibuddimRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (roleId: string) => eventOpsApi.deleteKibuddimRole(roleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kibuddim'] });
    },
  });
}
